import { useState, useEffect } from 'react';
import {
  signIn as amplifySignIn,
  signUp as amplifySignUp,
  signOut as amplifySignOut,
  confirmSignUp as amplifyConfirmSignUp,
  confirmSignIn as amplifyConfirmSignIn,
  resetPassword as amplifyResetPassword,
  confirmResetPassword as amplifyConfirmResetPassword,
  getCurrentUser,
  fetchAuthSession,
  type AuthUser,
} from 'aws-amplify/auth';
import posthog from 'posthog-js';

interface UseAuthReturn {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<{ newPasswordRequired: boolean }>;
  signUp: (email: string, password: string) => Promise<void>;
  confirmSignUp: (email: string, code: string) => Promise<void>;
  confirmNewPassword: (newPassword: string) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  confirmResetPassword: (email: string, code: string, newPassword: string) => Promise<void>;
  signOut: () => Promise<void>;
  getIdToken: () => Promise<string | undefined>;
}

export function useAuth(): UseAuthReturn {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    getCurrentUser()
      .then((u) => {
        setUser(u);
        posthog.identify(u.userId, { username: u.username });
      })
      .catch(() => setUser(null))
      .finally(() => setIsLoading(false));
  }, []);

  const signIn = async (email: string, password: string) => {
    // Clear any stale session before signing in
    try { await amplifySignOut(); } catch {}
    const result = await amplifySignIn({ username: email, password });
    if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
      return { newPasswordRequired: true };
    }
    const currentUser = await getCurrentUser();
    setUser(currentUser);
    posthog.identify(currentUser.userId, { username: currentUser.username });
    return { newPasswordRequired: false };
  };

  const confirmNewPassword = async (newPassword: string) => {
    await amplifyConfirmSignIn({ challengeResponse: newPassword });
    const currentUser = await getCurrentUser();
    setUser(currentUser);
  };

  const signUp = async (email: string, password: string) => {
    await amplifySignUp({
      username: email,
      password,
      options: { userAttributes: { email } },
    });
  };

  const confirmSignUp = async (email: string, code: string) => {
    await amplifyConfirmSignUp({ username: email, confirmationCode: code });
  };

  const resetPassword = async (email: string) => {
    await amplifyResetPassword({ username: email });
  };

  const confirmResetPassword = async (email: string, code: string, newPassword: string) => {
    await amplifyConfirmResetPassword({ username: email, confirmationCode: code, newPassword });
  };

  const signOut = async () => {
    await amplifySignOut();
    posthog.reset();
    setUser(null);
  };

  const getIdToken = async () => {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString();
  };

  return {
    user,
    isAuthenticated: !!user,
    isLoading,
    signIn,
    signUp,
    confirmSignUp,
    confirmNewPassword,
    resetPassword,
    confirmResetPassword,
    signOut,
    getIdToken,
  };
}
